import Response from '../modules/classes/Response';
import Group from '../models/Group';

class GroupController {
    constructor(apiService) {
        this.apiService = apiService;
    }

    getService() {
        return this.apiService ? this.apiService : require('../services/APIService').default;
    }

    /**
     * Create a new group
     * @param {Object} groupData - Group creation data
     * @returns {Promise<Response>} - Response containing created group
     */
    async createGroup(groupData) {
        try {
            const response = await this.getService().requestWithRawJson('/groups', 'POST', groupData);
            
            if (response.data) {
                const group = Group.fromAPI(response.data);
                return new Response({
                    success: true,
                    data: group,
                    message: response.message || 'Group created successfully',
                });
            }
            
            return new Response({
                success: false,
                message: response.message || 'Failed to create group',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to create group',
                error,
            });
        }
    }

    async getGroupsAsAdmin(params = {}) {
        try {
            const response = await this.getService().requestWithParams('/groups/admin/all', 'GET', params);

            if (response.data) {
                return new Response({
                    success: true,
                    data: response.data,
                    message: response.message || 'Groups retrieved successfully',
                });
            }

            return new Response({
                success: false,
                message: response.message || 'Failed to retrieve groups',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to retrieve groups',
                error,
            });
        }
    }

    /**
     * Accept invite for this group (current user)
     * @param {string} groupId - Group ID
     * @returns {Promise<Response>} - Response confirming acceptance
     */
    async acceptInviteForGroup(groupId) {
        try {
            const response = await this.getService().post(`/groups/${groupId}/invites/accept`);

            return new Response({
                success: true,
                data: response.data,
                message: response.message || 'Invite accepted successfully',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to accept invite',
                error,
            });
        }
    }

    /**
     * Invite a user to a group by username
     * @param {string} groupId - Group ID
     * @param {string} username - Username to invite
     * @param {string} message - Optional invite message
     * @returns {Promise<Response>}
     */
    async inviteMemberByUsername(groupId, username, message = '') {
        try {
            const response = await this.getService().post(`/groups/${groupId}/invites/username`, {
                username,
                message,
            });

            return new Response({
                success: true,
                data: response.data,
                message: response.message || 'Invite created successfully',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to invite user',
                error,
            });
        }
    }

    /**
     * Get pending group invitations for the current user
     * @returns {Promise<Response>}
     */
    async getPendingInvitesForUser() {
        try {
            const response = await this.getService().get('/groups/invites/pending');

            return new Response({
                success: true,
                data: response.data,
                message: response.message || 'Pending group invitations retrieved successfully',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to load pending invitations',
                error,
            });
        }
    }

    /**
     * Decline invite for this group (current user)
     * @param {string} groupId - Group ID
     * @returns {Promise<Response>}
     */
    async declineInviteForGroup(groupId) {
        try {
            const response = await this.getService().post(`/groups/${groupId}/invites/decline`);

            return new Response({
                success: true,
                data: response.data,
                message: response.message || 'Invite declined successfully',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to decline invite',
                error,
            });
        }
    }

    /**
     * Get all groups with pagination
     * @param {Object} params - Query parameters (page, limit, search, etc.)
     * @returns {Promise<Response>} - Response containing groups list
     */
    async getGroups(params = {}) {
        try {
            const response = await this.getService().requestWithParams('/groups', 'GET', params);
            
            if (response.data) {
                return new Response({
                    success: true,
                    data: response.data,
                    message: response.message || 'Groups retrieved successfully',
                });
            }
            
            return new Response({
                success: false,
                message: response.message || 'Failed to retrieve groups',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to retrieve groups',
                error,
            });
        }
    }

    /**
     * Get group by ID
     * @param {string} groupId - Group ID
     * @returns {Promise<Response>} - Response containing group data
     */
    async getGroupById(groupId) {
        try {
            const res = await this.getService().get(`/groups/${groupId}`);
            return Response.parse(res);
        } catch (error) {
            return Response.error(error.message || 'Failed to retrieve group');
        }
    }

    /**
     * Get group by slug
     * @param {string} slug - Group slug
     * @returns {Promise<Response>} - Response containing group data
     */
    async getGroupBySlug(slug) {
        try {
            const response = await this.getService().get(`/groups/slug/${slug}`);
            
            if (response.data) {
                const group = Group.fromAPI(response.data);
                return new Response({
                    success: true,
                    data: group,
                    message: response.message || 'Group retrieved successfully',
                });
            }
            
            return new Response({
                success: false,
                message: response.message || 'Group not found',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to retrieve group',
                error,
            });
        }
    }

    /**
     * Update group
     * @param {string} groupId - Group ID
     * @param {Object} updateData - Group update data
     * @returns {Promise<Response>} - Response containing updated group
     */
    async updateGroup(groupId, updateData) {
        try {
            const response = await this.getService().requestWithRawJson(`/groups/${groupId}`, 'PUT', updateData);
            
            if (response.data) {
                const group = Group.fromAPI(response.data);
                return new Response({
                    success: true,
                    data: group,
                    message: response.message || 'Group updated successfully',
                });
            }
            
            return new Response({
                success: false,
                message: response.message || 'Failed to update group',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to update group',
                error,
            });
        }
    }

    /**
     * Delete group
     * @param {string} groupId - Group ID
     * @returns {Promise<Response>} - Response confirming deletion
     */
    async deleteGroup(groupId) {
        try {
            const response = await this.getService().delete(`/groups/${groupId}`);
            
            return new Response({
                success: true,
                data: response.data,
                message: response.message || 'Group deleted successfully',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to delete group',
                error,
            });
        }
    }

    /**
     * Join a group
     * @param {string} groupId - Group ID
     * @returns {Promise<Response>} - Response confirming join
     */
    async joinGroup(groupId) {
        try {
            const response = await this.getService().post(`/groups/${groupId}/join`);
            
            return new Response({
                success: true,
                data: response.data,
                message: response.message || 'Successfully joined group',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to join group',
                error,
            });
        }
    }

    /**
     * Leave a group
     * @param {string} groupId - Group ID
     * @returns {Promise<Response>} - Response confirming leave
     */
    async leaveGroup(groupId) {
        try {
            const response = await this.getService().post(`/groups/${groupId}/leave`);
            
            return new Response({
                success: true,
                data: response.data,
                message: response.message || 'Successfully left group',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to leave group',
                error,
            });
        }
    }

    /**
     * Follow a group
     * @param {string} groupId - Group ID
     * @returns {Promise<Response>} - Response confirming follow
     */
    async followGroup(groupId) {
        try {
            const response = await this.getService().post(`/groups/${groupId}/follow`);
            
            return new Response({
                success: true,
                data: response.data,
                message: response.message || 'Successfully followed group',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to follow group',
                error,
            });
        }
    }

    /**
     * Unfollow a group
     * @param {string} groupId - Group ID
     * @returns {Promise<Response>} - Response confirming unfollow
     */
    async unfollowGroup(groupId) {
        try {
            const response = await this.getService().post(`/groups/${groupId}/unfollow`);
            
            return new Response({
                success: true,
                data: response.data,
                message: response.message || 'Successfully unfollowed group',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to unfollow group',
                error,
            });
        }
    }

    /**
     * Upload group avatar
     * @param {string} groupId - Group ID
     * @param {Object} file - File data
     * @returns {Promise<Response>} - Response with upload result
     */
    async uploadAvatar(groupId, file) {
        try {
            const formData = new FormData();
            formData.append('avatar', file);

            const res = await this.getService().requestWithFormData(`/groups/${groupId}`, 'PUT', formData);
            return Response.parse(res);
        } catch (error) {
            return Response.error(error.message || 'Failed to upload avatar');
        }
    }

    /**
     * Upload group banner
     * @param {string} groupId - Group ID
     * @param {Object} file - File data
     * @returns {Promise<Response>} - Response with upload result
     */
    async uploadBanner(groupId, file) {
        try {
            const formData = new FormData();
            formData.append('banner', file);

            const res = await this.getService().requestWithFormData(`/groups/${groupId}`, 'PUT', formData);
            return Response.parse(res);
        } catch (error) {
            return Response.error(error.message || 'Failed to upload banner');
        }
    }

    /**
     * Get group members
     * @param {string} groupId - Group ID
     * @param {Object} params - Query parameters (page, limit, status)
     * @returns {Promise<Response>} - Response with members list
     */
    async getGroupMembers(groupId, params = {}) {
        try {
            const response = await this.getService().requestWithParams(`/groups/${groupId}/members`, 'GET', params);
            
            return new Response({
                success: true,
                data: response.data,
                message: response.message || 'Members retrieved successfully',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to retrieve members',
                error,
            });
        }
    }

    /**
     * Update member role
     * @param {string} groupId - Group ID
     * @param {string} userId - User ID
     * @param {string} role - New role (member, moderator, admin)
     * @returns {Promise<Response>} - Response confirming update
     */
    async updateMemberRole(groupId, userId, role) {
        try {
            const response = await this.getService().requestWithRawJson(
                `/groups/${groupId}/members/${userId}/role`, 
                'PUT', 
                { role }
            );
            
            return new Response({
                success: true,
                data: response.data,
                message: response.message || 'Member role updated successfully',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to update member role',
                error,
            });
        }
    }

    /**
     * Remove member from group
     * @param {string} groupId - Group ID
     * @param {string} userId - User ID to remove
     * @returns {Promise<Response>} - Response confirming removal
     */
    async removeMember(groupId, userId) {
        try {
            const response = await this.getService().delete(`/groups/${groupId}/members/${userId}`);
            
            return new Response({
                success: true,
                data: response.data,
                message: response.message || 'Member removed successfully',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to remove member',
                error,
            });
        }
    }

    /**
     * Ban member from group
     * @param {string} groupId - Group ID
     * @param {string} userId - User ID to ban
     * @param {string} reason - Ban reason (optional)
     * @returns {Promise<Response>} - Response confirming ban
     */
    async banMember(groupId, userId, reason = null) {
        try {
            const response = await this.getService().requestWithRawJson(
                `/groups/${groupId}/members/${userId}/ban`, 
                'POST', 
                { reason }
            );
            
            return new Response({
                success: true,
                data: response.data,
                message: response.message || 'Member banned successfully',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to ban member',
                error,
            });
        }
    }

    /**
     * Unban member from group
     * @param {string} groupId - Group ID
     * @param {string} userId - User ID to unban
     * @returns {Promise<Response>} - Response confirming unban
     */
    async unbanMember(groupId, userId) {
        try {
            const response = await this.getService().post(`/groups/${groupId}/members/${userId}/unban`);
            
            return new Response({
                success: true,
                data: response.data,
                message: response.message || 'Member unbanned successfully',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to unban member',
                error,
            });
        }
    }

    /**
     * Transfer group ownership
     * @param {string} groupId - Group ID
     * @param {string} newOwnerId - New owner user ID
     * @returns {Promise<Response>} - Response confirming transfer
     */
    async transferOwnership(groupId, newOwnerId) {
        try {
            const response = await this.getService().requestWithRawJson(
                `/groups/${groupId}/transfer-ownership`, 
                'POST', 
                { newOwnerId }
            );
            
            return new Response({
                success: true,
                data: response.data,
                message: response.message || 'Ownership transferred successfully',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to transfer ownership',
                error,
            });
        }
    }

    /**
     * Get pending members
     * @param {string} groupId - Group ID
     * @returns {Promise<Response>} - Response with pending members list
     */
    async getPendingMembers(groupId) {
        try {
            const response = await this.getService().get(`/groups/${groupId}/members/pending`);
            
            return new Response({
                success: true,
                data: response.data,
                message: response.message || 'Pending members retrieved successfully',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to get pending members',
                error,
            });
        }
    }

    /**
     * Approve pending member
     * @param {string} groupId - Group ID
     * @param {string} userId - User ID to approve
     * @returns {Promise<Response>} - Response confirming approval
     */
    async approveMember(groupId, userId) {
        try {
            const response = await this.getService().post(`/groups/${groupId}/members/${userId}/approve`);
            
            return new Response({
                success: true,
                data: response.data,
                message: response.message || 'Member approved successfully',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to approve member',
                error,
            });
        }
    }

    /**
     * Reject pending member
     * @param {string} groupId - Group ID
     * @param {string} userId - User ID to reject
     * @returns {Promise<Response>} - Response confirming rejection
     */
    async rejectMember(groupId, userId) {
        try {
            const response = await this.getService().post(`/groups/${groupId}/members/${userId}/reject`);
            
            return new Response({
                success: true,
                data: response.data,
                message: response.message || 'Member rejected successfully',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to reject member',
                error,
            });
        }
    }

    /**
     * Track a group page view
     * @param {string} groupId - Group ID
     * @returns {Promise<Response>} - Response indicating if view was tracked
     */
    async trackGroupView(groupId) {
        try {
            const response = await this.getService().post(`/groups/${groupId}/track-view`);
            return new Response({
                success: true,
                data: response.data?.data || response.data,
                message: 'Group view tracked',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to track group view',
                error,
            });
        }
    }
}

// Create default instance
const defaultGroupController = new GroupController(null);

export { GroupController, defaultGroupController };
export default GroupController;